import { useState } from "react";
import { TRANSACTION_TYPES } from "./crmPipelineShared";

const field = "w-full bg-[#091E16] border border-white/15 focus:border-brass rounded-lg px-3 py-2.5 text-sm text-white outline-none";
const today = () => new Date().toISOString().slice(0, 10);

export function LostDialog({ onCancel, onConfirm, title = "Mark as Lost" }) {
  const [reason, setReason] = useState("");
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="lost-dialog">
      <div className="absolute inset-0 bg-black/60" onClick={onCancel} />
      <div className="relative w-full max-w-sm bg-[#0B241B] border border-white/10 rounded-2xl p-5">
        <h3 className="text-base font-serif text-white mb-3">{title}</h3>
        <label className="block text-xs text-emerald-100/60 mb-1">Reason (optional)</label>
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} data-testid="lost-reason"
          className={field} placeholder="e.g. Sold with another agent" />
        <div className="flex gap-2 mt-4">
          <button onClick={onCancel} className="flex-1 rounded-full py-2.5 text-sm bg-white/5 text-emerald-100/80 hover:bg-white/10">Cancel</button>
          <button onClick={() => onConfirm(reason)} data-testid="lost-confirm" className="flex-1 rounded-full py-2.5 text-sm bg-red-500/80 hover:bg-red-500 text-white font-semibold">Confirm</button>
        </div>
      </div>
    </div>
  );
}

export function CompletedDialog({ initial = {}, onCancel, onConfirm }) {
  const [form, setForm] = useState({
    transaction_type: initial.transaction_type || TRANSACTION_TYPES[0],
    price: initial.price ?? "",
    fee: initial.fee ?? "",
    completion_date: initial.completion_date || today(),
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="completed-dialog">
      <div className="absolute inset-0 bg-black/60" onClick={onCancel} />
      <div className="relative w-full max-w-sm bg-[#0B241B] border border-white/10 rounded-2xl p-5">
        <h3 className="text-base font-serif text-white mb-1">Record completed deal</h3>
        <p className="text-xs text-emerald-100/50 mb-4">Capture the revenue so it shows in Conversion Analytics.</p>
        <div className="space-y-3">
          <div>
            <label className="block text-xs text-emerald-100/60 mb-1">Transaction type</label>
            <select value={form.transaction_type} onChange={set("transaction_type")} data-testid="completed-type" className={field}>
              {TRANSACTION_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-emerald-100/60 mb-1">Deal value (£)</label>
              <input type="number" inputMode="decimal" value={form.price} onChange={set("price")} data-testid="completed-price" className={field} placeholder="185000" />
            </div>
            <div>
              <label className="block text-xs text-emerald-100/60 mb-1">EC fee (£)</label>
              <input type="number" inputMode="decimal" value={form.fee} onChange={set("fee")} data-testid="completed-fee" className={field} placeholder="3000" />
            </div>
          </div>
          <div>
            <label className="block text-xs text-emerald-100/60 mb-1">Completion date</label>
            <input type="date" value={form.completion_date} onChange={set("completion_date")} data-testid="completed-date" className={field} />
          </div>
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onCancel} className="flex-1 rounded-full py-2.5 text-sm bg-white/5 text-emerald-100/80 hover:bg-white/10">Cancel</button>
          <button onClick={() => onConfirm({ ...form, price: parseFloat(form.price) || 0, fee: parseFloat(form.fee) || 0 })}
            data-testid="completed-confirm" className="flex-1 rounded-full py-2.5 text-sm bg-emerald-500/80 hover:bg-emerald-500 text-white font-semibold">Record Deal</button>
        </div>
      </div>
    </div>
  );
}

export function AppointmentDialog({ initial = "", onCancel, onConfirm }) {
  const [dt, setDt] = useState(initial || "");
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="appointment-dialog">
      <div className="absolute inset-0 bg-black/60" onClick={onCancel} />
      <div className="relative w-full max-w-sm bg-[#0B241B] border border-white/10 rounded-2xl p-5">
        <h3 className="text-base font-serif text-white mb-1">Book appointment</h3>
        <p className="text-xs text-emerald-100/50 mb-4">Pick a date &amp; time — it will show in Upcoming Appointments.</p>
        <label className="block text-xs text-emerald-100/60 mb-1">Appointment date &amp; time</label>
        <input type="datetime-local" value={dt} min={new Date().toISOString().slice(0, 16)} onChange={(e) => setDt(e.target.value)} data-testid="appointment-datetime" className={field} />
        <div className="flex gap-2 mt-5">
          <button onClick={onCancel} className="flex-1 rounded-full py-2.5 text-sm bg-white/5 text-emerald-100/80 hover:bg-white/10">Cancel</button>
          <button onClick={() => onConfirm(dt)} data-testid="appointment-confirm" className="flex-1 rounded-full py-2.5 text-sm bg-brass hover:bg-brass-600 text-emerald-950 font-semibold">Book Appointment</button>
        </div>
      </div>
    </div>
  );
}
